import type { FriendInfo } from '@danda/shared';
import type { LobbyState } from './lobbyStore.types';

/**
 * Friends-system slice: the account-level friends list with presence.
 * Hydrated from `lobby:friends-list-result`; individual entries are upserted
 * on `lobby:friend-added` and dropped on `lobby:friend-removed`, so a
 * friendship change shows without a refetch.
 */
export const friendsSlice = {
  friends: [] as FriendInfo[],
  friendsHydrated: false,

  setFriends: (friends: FriendInfo[]) => ({ friends, friendsHydrated: true }),

  removeFriend:
    (friendUserId: string) =>
    (state: LobbyState): Partial<LobbyState> => ({
      friends: state.friends.filter((f) => f.userId !== friendUserId),
    }),

  // Deltas mutate the list but never establish hydration: only the
  // `friends-list-result` snapshot (via setFriends) means the list is complete,
  // so a lone `friend-added` racing ahead of it can't flash a partial list as
  // authoritative. The connection layer always requests that snapshot on open.
  upsertFriend:
    (friend: FriendInfo) =>
    (state: LobbyState): Partial<LobbyState> => {
      const existing = state.friends.find((f) => f.userId === friend.userId);
      if (existing) {
        return {
          friends: state.friends.map((f) => (f.userId === friend.userId ? friend : f)),
        };
      }
      return { friends: [...state.friends, friend] };
    },
};
